import { useState, useEffect } from 'react';
import { Plus, RefreshCw, Edit, Trash2, CheckCircle, ArrowUpDown, Settings, Monitor, Users } from 'lucide-react';
import { useTenantStore } from '../stores/tenantStore';
import DataTable, { type Column } from '../components/common/DataTable';
import StatCard from '../components/common/StatCard';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { useToast } from '../components/common/ToastContainer';
import { routingPolicyService, applicationService } from '../services';
import type { RoutingPolicy, ApplicationDefinition, ApplicationGroup } from '../services';
import PolicyModal from '../components/policies/PolicyModal';
import PolicyDetailsModal from '../components/policies/PolicyDetailsModal';
import { usePermissions } from '../hooks/usePermissions';

const RoutingPolicies = () => {
  const toast = useToast();
  const { canCreate, canUpdate, canDelete } = usePermissions();
  const [policies, setPolicies] = useState<RoutingPolicy[]>([]);
  const [applications, setApplications] = useState<ApplicationDefinition[]>([]);
  const [appGroups, setAppGroups] = useState<ApplicationGroup[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page] = useState(1);
  const [pageSize] = useState(100);

  const selectedTenantId = useTenantStore((state) => state.selectedTenantId);

  // Modal states
  const [isPolicyModalOpen, setIsPolicyModalOpen] = useState(false);
  const [isPolicyDetailsOpen, setIsPolicyDetailsOpen] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState<RoutingPolicy | null>(null);
  const [selectedPolicy, setSelectedPolicy] = useState<RoutingPolicy | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ show: boolean; policy?: RoutingPolicy }>({ show: false });
  const [deleting, setDeleting] = useState(false);

  // Fetch routing policies
  const fetchPolicies = async () => {
    setIsLoading(true);
    try {
      const response = await routingPolicyService.list({
        page,
        pageSize,
        tenantId: selectedTenantId
      });
      // Store all policies for edge-use counting, but display only templates
      setPolicies(response.items);
    } catch (error) {
      console.error('Failed to fetch routing policies:', error);
      toast.error('Load Failed', 'Failed to load routing policies');
    } finally {
      setIsLoading(false);
    }
  };

  // Templates = policies with no edge_id AND not applied DGAPs (which have source_template_id)
  const templates = policies.filter(p => !p.edge_id && !(p.scope === 'device_group' && p.source_template_id));
  // Edge copies = policies with edge_id (applied to specific edges)
  const edgeCopies = policies.filter(p => !!p.edge_id);

  // Count how many edges use each template (by matching name)
  const getEdgeUseCount = (templateName: string): number =>
    edgeCopies.filter(p => p.name === templateName).length;

  // Fetch applications and groups for dropdowns
  const fetchApplications = async () => {
    try {
      const [appsResponse, groupsResponse] = await Promise.all([
        applicationService.list({ page: 1, pageSize: 100, tenantId: selectedTenantId }),
        applicationService.listGroups({ page: 1, pageSize: 100, tenantId: selectedTenantId }),
      ]);
      setApplications(appsResponse.items);
      setAppGroups(groupsResponse.items);
    } catch (error) {
      console.error('Failed to fetch applications:', error);
    }
  };

  useEffect(() => {
    fetchPolicies();
    fetchApplications();
  }, [page, pageSize, selectedTenantId]);

  const handleRefresh = () => {
    fetchPolicies();
    fetchApplications();
  };

  // CRUD Handlers
  const handleSavePolicy = async (data: any) => {
    try {
      if (editingPolicy) {
        await routingPolicyService.update(editingPolicy.id, data);
        toast.success('Policy Updated', 'Routing policy updated successfully');
      } else {
        // Include tenant_id for SuperAdmin context
        const createData = { ...data };
        if (selectedTenantId) {
          createData.tenant_id = selectedTenantId;
        }
        await routingPolicyService.create(createData);
        toast.success('Policy Created', 'Routing policy created successfully');
      }
      setIsPolicyModalOpen(false);
      setEditingPolicy(null);
      fetchPolicies();
    } catch (error: any) {
      console.error('Failed to save routing policy:', error);
      const saveMsg = error?.response?.data?.error?.message || error?.response?.data?.error || 'Failed to save routing policy';
      toast.error('Save Failed', typeof saveMsg === 'string' ? saveMsg : 'Failed to save routing policy');
    }
  };

  const handleDeletePolicy = async () => {
    if (!deleteConfirm.policy) return;

    try {
      setDeleting(true);
      await routingPolicyService.delete(deleteConfirm.policy.id);
      toast.success('Policy Deleted', 'Routing policy deleted successfully');
      setDeleteConfirm({ show: false });
      fetchPolicies();
    } catch (error: any) {
      console.error('Failed to delete routing policy:', error);
      const deleteMsg = error?.response?.data?.error?.message || error?.response?.data?.error || 'Failed to delete routing policy';
      toast.error('Delete Failed', typeof deleteMsg === 'string' ? deleteMsg : 'Failed to delete routing policy');
    } finally {
      setDeleting(false);
    }
  };

  const handleViewPolicy = async (policy: RoutingPolicy) => {
    try {
      // Fetch full policy details with rules
      const fullPolicy = await routingPolicyService.getById(policy.id);
      setSelectedPolicy(fullPolicy);
      setIsPolicyDetailsOpen(true);
    } catch (error) {
      console.error('Failed to fetch policy details:', error);
      toast.error('Load Failed', 'Failed to load policy details');
    }
  };

  // Policy columns
  const policyColumns: Column<RoutingPolicy>[] = [
    {
      key: 'name',
      label: 'Policy Name',
      sortable: true,
      render: (row) => (
        <div>
          <button
            onClick={() => handleViewPolicy(row)}
            className="flex items-center space-x-2 text-left hover:text-primary-600 transition-colors"
          >
            <span className="font-semibold">{row.name}</span>
            {row.scope === 'device_group' ? (
              <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-purple-100 text-purple-700">Access Policy</span>
            ) : row.scope === 'dg_template' ? (
              <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-indigo-100 text-indigo-700">DG Template</span>
            ) : row.scope === 'edge' ? (
              <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-green-100 text-green-700">Edge</span>
            ) : (
              <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-100 text-blue-700">Template</span>
            )}
          </button>
          {row.description && <div className="text-xs text-gray-500">{row.description}</div>}
        </div>
      ),
    },
    {
      key: 'priority',
      label: 'Priority',
      sortable: true,
      render: (row) => (
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${row.priority < 50 ? 'bg-red-100 text-red-700' :
          row.priority < 100 ? 'bg-yellow-100 text-yellow-700' :
            'bg-green-100 text-green-700'
          }`}>
          {row.priority}
        </span>
      ),
    },
    {
      key: 'rules',
      label: 'Rules',
      render: (row) => (
        <span className="text-sm text-gray-600">
          {row.rules?.length || 0} rule{row.rules?.length !== 1 ? 's' : ''}
        </span>
      ),
    },
    {
      key: 'edge_uses',
      label: 'Edge Uses',
      render: (row) => {
        const count = getEdgeUseCount(row.name);
        return (
          <span className={`text-sm ${count > 0 ? 'text-primary-600 font-medium' : 'text-gray-400'}`}>
            {count} edge{count !== 1 ? 's' : ''}
          </span>
        );
      },
    },
    {
      key: 'enabled',
      label: 'Status',
      sortable: true,
      render: (row) => (
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${row.enabled === 1 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
          }`}>
          {row.enabled === 1 ? 'Enabled' : 'Disabled'}
        </span>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (row) => (
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handleViewPolicy(row)}
            className="p-1 hover:bg-gray-100 rounded text-blue-600"
            title="View Details"
          >
            <Settings className="w-4 h-4" />
          </button>
          {canUpdate && (
            <button
              onClick={() => { setEditingPolicy(row); setIsPolicyModalOpen(true); }}
              className="p-1 hover:bg-gray-100 rounded text-primary-600"
              title="Edit Policy"
            >
              <Edit className="w-4 h-4" />
            </button>
          )}
          {canDelete && (
            <button
              onClick={() => setDeleteConfirm({ show: true, policy: row })}
              className="p-1 hover:bg-gray-100 rounded text-red-600"
              title="Delete Policy"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Routing Policies</h1>
          <p className="text-gray-600 mt-1">Define routing policy templates and device-group policies</p>
        </div>
        <div className="flex items-center space-x-3">
          <button onClick={handleRefresh} className="btn-secondary inline-flex items-center space-x-2">
            <RefreshCw className="w-4 h-4" />
            <span>Refresh</span>
          </button>
          {canCreate && (
            <button
              onClick={() => { setEditingPolicy(null); setIsPolicyModalOpen(true); }}
              className="btn-primary inline-flex items-center space-x-2"
            >
              <Plus className="w-4 h-4" />
              <span>New Policy</span>
            </button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <StatCard
          icon={Settings}
          title="Templates"
          value={isLoading ? '...' : templates.filter(p => p.scope !== 'device_group' && p.scope !== 'dg_template').length}
          color="primary"
        />
        <StatCard
          icon={Users}
          title="Access Policies"
          value={isLoading ? '...' : templates.filter(p => p.scope === 'device_group' || p.scope === 'dg_template').length}
          color="secondary"
        />
        <StatCard
          icon={CheckCircle}
          title="Active"
          value={isLoading ? '...' : templates.filter(p => p.enabled === 1).length}
          color="success"
        />
        <StatCard
          icon={ArrowUpDown}
          title="Total Rules"
          value={isLoading ? '...' : templates.reduce((sum, p) => sum + (p.rules?.length || 0), 0)}
          color="info"
        />
        <StatCard
          icon={Monitor}
          title="Edge Deployments"
          value={isLoading ? '...' : edgeCopies.length}
          color="warning"
        />
      </div>

      {/* Policies Table */}
      <div className="card">
        <div className="p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Policy Templates</h2>
          <DataTable
            columns={policyColumns}
            data={templates}
            searchPlaceholder="Search templates..."
          />
        </div>
      </div>

      {/* Modals */}
      <PolicyModal
        isOpen={isPolicyModalOpen}
        onClose={() => { setIsPolicyModalOpen(false); setEditingPolicy(null); }}
        onSave={handleSavePolicy}
        policy={editingPolicy}
        mode={editingPolicy ? 'edit' : 'add'}
      />

      {selectedPolicy && (
        <PolicyDetailsModal
          isOpen={isPolicyDetailsOpen}
          onClose={() => { setIsPolicyDetailsOpen(false); setSelectedPolicy(null); }}
          policy={selectedPolicy}
          applications={applications}
          appGroups={appGroups}
          onRefresh={() => {
            fetchPolicies();
            if (selectedPolicy) {
              routingPolicyService.getById(selectedPolicy.id).then(fullPolicy => setSelectedPolicy(fullPolicy));
            }
          }}
        />
      )}

      <ConfirmDialog
        isOpen={deleteConfirm.show}
        onClose={() => setDeleteConfirm({ show: false })}
        onConfirm={handleDeletePolicy}
        title="Delete Routing Policy"
        message={`Are you sure you want to delete the policy "${deleteConfirm.policy?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
        isLoading={deleting}
      />

    </div>
  );
};

export default RoutingPolicies;
